import React, { useState } from 'react';
import axios from 'axios';
import { useDialog } from '../DialogContext';
import { API_BASE_URL } from '../config';
import { formatDay } from '../dashboard/range';
import LineItems from './LineItems';
import PaymentModal from './PaymentModal';
import { invoiceState } from './overview';
import { formatMoney, formatMonthLong, formatStorage, paymentMethodLabel } from './format';

// Status is never color alone: every chip says what it means in words, and an
// overdue one carries an icon as well.
const STATUS_STYLES = {
  paid: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  open: 'bg-sky-500/10 text-sky-300 border-sky-500/30',
  overdue: 'bg-red-500/15 text-red-300 border-red-500/40',
  void: 'bg-gray-600/20 text-gray-400 border-gray-600/40',
};

export const InvoiceStatus = ({ invoice }) => {
  const state = invoiceState(invoice);
  const label = {
    paid: 'Paid',
    open: 'Open',
    overdue: `${invoice.days_overdue} day${invoice.days_overdue === 1 ? '' : 's'} overdue`,
    void: 'Void',
  }[state];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${STATUS_STYLES[state]}`}>
      {state === 'overdue' && (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
      )}
      {label}
    </span>
  );
};

// One line under the amount: when it was paid, or when it's due.
export const dueLine = (invoice) => {
  if (invoice.status === 'paid') return invoice.paid_at ? `Paid ${formatDay(invoice.paid_at.slice(0, 10))}` : 'Paid';
  if (invoice.status === 'void') return 'Voided';
  const partly = invoice.amount_paid_cents > 0 ? `${formatMoney(invoice.balance_cents)} left · ` : '';
  return `${partly}Due ${formatDay(invoice.due_on, { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

const UsageRow = ({ label, value, of }) => (
  <div className="flex items-baseline justify-between gap-3 py-1">
    <dt className="text-gray-400">{label}</dt>
    <dd className="text-gray-200 tabular-nums text-right">
      {value}
      {of && <span className="text-gray-500"> of {of}</span>}
    </dd>
  </div>
);

// The body of an expanded invoice: its lines, the usage it was priced from, and
// the payments against it. ``invoice`` must be the detail form, with lines.
export const InvoiceDetail = ({ invoice }) => {
  const usage = invoice.usage || {};
  const included = usage.allowances || {};
  return (
    <div className="grid gap-6 lg:grid-cols-5">
      <div className="lg:col-span-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
          {formatMonthLong(invoice.period)} · {invoice.plan_name} plan
        </h4>
        <LineItems bill={invoice} compact />
      </div>
      <div className="lg:col-span-2 space-y-5">
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Usage billed</h4>
          <dl className="text-sm">
            <UsageRow label="Seats" value={Number(usage.seats || 0).toLocaleString()} />
            <UsageRow
              label="Submissions"
              value={Number(usage.submissions || 0).toLocaleString()}
              of={included.submissions != null ? Number(included.submissions).toLocaleString() : null}
            />
            <UsageRow
              label="Guest submissions"
              value={Number(usage.guest_submissions || 0).toLocaleString()}
              of={included.guest_submissions != null ? Number(included.guest_submissions).toLocaleString() : null}
            />
            <UsageRow
              label="Photo storage"
              value={formatStorage(usage.storage_bytes)}
              of={included.storage_bytes != null ? formatStorage(included.storage_bytes) : null}
            />
            {usage.resubmissions > 0 && (
              <UsageRow label="Re-submissions (free)" value={Number(usage.resubmissions).toLocaleString()} />
            )}
          </dl>
        </div>
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Payments</h4>
          {invoice.payments?.length ? (
            <ul className="text-sm divide-y divide-gray-700/60">
              {invoice.payments.map((payment) => (
                <li key={payment.id} className="flex items-baseline justify-between gap-3 py-1.5">
                  <span className="text-gray-300">
                    {formatDay(payment.received_on, { month: 'short', day: 'numeric', year: 'numeric' })}
                    <span className="text-gray-500"> · {paymentMethodLabel(payment.method)}{payment.reference ? ` ${payment.reference}` : ''}</span>
                  </span>
                  <span className="text-gray-200 tabular-nums">{formatMoney(payment.amount_cents)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">{invoice.status === 'void' ? 'Voided before any payment.' : 'None yet.'}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Record-payment, void and re-issue for any invoice row. ``onChanged`` runs
// after each one succeeds, so the caller can reload what it shows.
export const useInvoiceActions = (onChanged) => {
  const { confirm, alert } = useDialog();
  const [paying, setPaying] = useState(null);

  const submitPayment = async (payload) => {
    try {
      await axios.post(`${API_BASE_URL}/api/billing/invoices/${paying.id}/payments`, payload);
      setPaying(null);
      onChanged();
      return null;
    } catch (err) {
      return err.response?.data?.msg || 'Could not record the payment.';
    }
  };

  const voidInvoice = async (invoice) => {
    const ok = await confirm({
      title: 'Void invoice',
      message: `Void ${invoice.number} for ${invoice.company_name || 'this company'} (${formatMoney(invoice.total_cents)})? The customer will no longer owe it, and ${formatMonthLong(invoice.period)} isn't billed again unless you re-issue it.`,
      confirmLabel: 'Void invoice',
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.post(`${API_BASE_URL}/api/billing/invoices/${invoice.id}/void`);
      onChanged();
    } catch (err) {
      alert({ title: 'Could not void', message: err.response?.data?.msg || 'Something went wrong.' });
    }
  };

  const reissue = async (invoice) => {
    const ok = await confirm({
      title: 'Re-issue invoice',
      message: `Bill ${invoice.company_name || 'this company'} for ${formatMonthLong(invoice.period)} again, at their current plan and terms? A new invoice replaces ${invoice.number}.`,
      confirmLabel: 'Re-issue',
    });
    if (!ok) return;
    try {
      await axios.post(`${API_BASE_URL}/api/billing/invoices/${invoice.id}/reissue`);
      onChanged();
    } catch (err) {
      alert({ title: 'Could not re-issue', message: err.response?.data?.msg || 'Something went wrong.' });
    }
  };

  return {
    recordPayment: setPaying,
    voidInvoice,
    reissue,
    modal: <PaymentModal invoice={paying} onConfirm={submitPayment} onCancel={() => setPaying(null)} />,
  };
};

const ACTION_BTN =
  'bg-gray-700/50 hover:bg-gray-600/60 text-gray-200 hover:text-white px-2.5 py-1 rounded-md text-xs font-medium border border-gray-600 hover:border-gray-500 transition-colors whitespace-nowrap';

// The buttons a dev gets on an invoice: only the ones that would succeed.
export const InvoiceActionButtons = ({ invoice, actions, liveSibling = false }) => (
  <div className="flex flex-wrap justify-end gap-1.5">
    {invoice.status === 'open' && (
      <button type="button" className={ACTION_BTN} onClick={() => actions.recordPayment(invoice)}>
        Record payment
      </button>
    )}
    {invoice.status !== 'void' && invoice.amount_paid_cents === 0 && (
      <button type="button" className={ACTION_BTN} onClick={() => actions.voidInvoice(invoice)}>
        Void
      </button>
    )}
    {invoice.status === 'void' && !liveSibling && (
      <button type="button" className={ACTION_BTN} onClick={() => actions.reissue(invoice)}>
        Re-issue
      </button>
    )}
  </div>
);
